<template>
  <div v-if="resolvedUrl" class="device-thumb" :class="thumbClass">
    <img :src="resolvedUrl" :alt="alt" class="device-thumb__img" draggable="false" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useImageAssetsStore } from '@/stores/imageAssets';

interface Props {
  url?: string | null;
  s3Key?: string | null;
  alt?: string;
  grayscale?: boolean;
  glow?: boolean;
  /**
   * How the picture fills its box. `cover` (the default) suits a photograph —
   * the motion sensor's room view fills its circle. `contain` is for a cut-out
   * PRODUCT, which must stay whole: the owner's rule (25.10.2026) "not cover",
   * "should display images iconic". Cover crops a pump's strainer pot off.
   */
  fit?: 'cover' | 'contain';
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  grayscale: false,
  glow: false,
  fit: 'cover',
});

const imageAssetsStore = useImageAssetsStore();

// Prefer a blob:// URL fetched via the images API (works in cloud/WebRTC mode)
// over the presigned MinIO URL that Electron cloud mode cannot reach.
const resolvedUrl = computed(() => {
  if (props.s3Key) {
    const blob = imageAssetsStore.getImageUrlByS3Key(props.s3Key);
    if (!blob) void imageAssetsStore.fetchImageUrlByS3Key(props.s3Key);
    return blob ?? props.url ?? null;
  }
  return props.url ?? null;
});

const thumbClass = computed(() => ({
  'device-thumb--grayscale': props.grayscale,
  'device-thumb--glow': props.glow,
  'device-thumb--contain': props.fit === 'contain',
}));
</script>

<style scoped>
.device-thumb {
  width: 2rem;
  height: 2rem;
  overflow: hidden;
  flex-shrink: 0;
}

.device-thumb__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: filter 0.3s ease;
}

/* A cut-out product stays whole inside the tile (owner, 25.10.2026: "not
   cover") — the circle then reads as an icon frame rather than a crop. */
.device-thumb--contain .device-thumb__img {
  object-fit: contain;
  object-position: center;
}

.device-thumb--grayscale .device-thumb__img {
  filter: grayscale(100%) brightness(0.6);
}

.device-thumb--glow {
  box-shadow: 0 0 10px 3px rgba(250, 204, 21, 0.3);
}
</style>
